import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useUserSearchList, useUserSearchGetFilters } from '../hooks/use-user-search.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { UserSearchResult, UserSearchFilters } from '../types/user-search.types';
import UserSearchComponent from '../components/user-search.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../../../../../states/user.atom';
import { rbac } from 'permissions/permissions';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { SelectedFilter } from '../../../../../types/filters.types';
import { useSearchParams } from 'react-router-dom';

/**
 * Parse filters from URL query string to SelectedFilter array
 */
const parseFiltersFromUrl = (filtersParam: string | null): SelectedFilter[] => {
  if (!filtersParam) return [];

  const filters: Record<string, string[]> = {};

  // Parse the filter string
  const filterPairs = filtersParam.split('&');
  filterPairs.forEach((pair) => {
    const [key, value] = pair.split('=');
    if (key && value) {
      // Match patterns like filters[employeeId][val][0]
      const match = key.match(/filters\[([^\]]+)\]\[val\]\[(\d+)\]/);
      if (match) {
        const filterKey = match[1];
        if (!filters[filterKey]) {
          filters[filterKey] = [];
        }
        filters[filterKey].push(decodeURIComponent(value));
      }
    }
  });

  // Convert to SelectedFilter format
  return Object.entries(filters).map(([key, values]) => ({
    key,
    values,
    operation: 'in' as const,
  }));
};

/**
 * Extract filter values from SelectedFilter array
 */
const extractFilterValues = (filters: SelectedFilter[]): UserSearchFilters => {
  const result: UserSearchFilters = {};

  filters.forEach((filter) => {
    if (filter.values && filter.values.length > 0) {
      const value = filter.values[0];
      switch (filter.key) {
        case 'employeeId':
          result.employeeNumber = value;
          break;
        case 'passportNumber':
          result.passportNumber = value;
          break;
        case 'applicationNumber':
          result.applicationNumber = value;
          break;
        case 'visaFileNumber':
          result.visaNumber = value;
          break;
        case 'visaUidNumber':
          result.uidNumber = value;
          break;
        case 'eidNumber':
          result.eidNumber = value;
          break;
      }
    }
  });

  return result;
};

/**
 * Convert UserSearchFilters to SelectedFilter array
 */
const filtersToSelectedFilters = (filters: UserSearchFilters): SelectedFilter[] => {
  const selectedFilters: SelectedFilter[] = [];

  if (filters.employeeNumber) {
    selectedFilters.push({
      key: 'employeeId',
      values: [filters.employeeNumber],
      operation: 'in',
    });
  }
  if (filters.passportNumber) {
    selectedFilters.push({
      key: 'passportNumber',
      values: [filters.passportNumber],
      operation: 'in',
    });
  }
  if (filters.applicationNumber) {
    selectedFilters.push({
      key: 'applicationNumber',
      values: [filters.applicationNumber],
      operation: 'in',
    });
  }
  if (filters.visaNumber) {
    selectedFilters.push({
      key: 'visaFileNumber',
      values: [filters.visaNumber],
      operation: 'in',
    });
  }
  if (filters.uidNumber) {
    selectedFilters.push({
      key: 'visaUidNumber',
      values: [filters.uidNumber],
      operation: 'in',
    });
  }
  if (filters.eidNumber) {
    selectedFilters.push({
      key: 'eidNumber',
      values: [filters.eidNumber],
      operation: 'in',
    });
  }

  return selectedFilters;
};

const UserSearchContainer = () => {
  const { t } = useTranslation();
  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();
  const [searchParams] = useSearchParams();

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  // Get filters from URL
  const filtersParam = searchParams.get('filters');
  const urlFilters = parseFiltersFromUrl(filtersParam);
  const initialFilters = extractFilterValues(urlFilters);

  // Local state for filter inputs (not applied until search is clicked)
  const [filters, setFilters] = useState<UserSearchFilters>(initialFilters);

  // State to track if search has been performed (to enable/disable API call)
  const [hasSearched, setHasSearched] = useState<boolean>(!!filtersParam);

  // Update local filters when URL filters change
  useEffect(() => {
    const urlFilters = parseFiltersFromUrl(filtersParam);
    const extractedFilters = extractFilterValues(urlFilters);
    setFilters(extractedFilters);
    setHasSearched(!!filtersParam);
  }, [filtersParam]);

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } =
    useUserSearchGetFilters();

  // Only fetch data if search has been performed
  const { data, isLoading, isFetching } = useUserSearchList(queryParams, {
    enabled: hasSearched,
  });
  const { docs: tableData = [] } = hasSearched
    ? (data ?? DEFAULT_PAGINATED_TABLE_DATA)
    : { docs: [] };

  const handleSearch = useCallback(
    (newFilters: UserSearchFilters) => {
      setFilters(newFilters);
      setHasSearched(true);
      // Convert to SelectedFilter format and update URL
      const selectedFilters = filtersToSelectedFilters(newFilters);
      handleFilterChange(selectedFilters);
    },
    [handleFilterChange]
  );

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        let sortOrder: 'asc' | 'desc';
        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          sortOrder = 'asc';
        }
        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const columns = useMemo<ColumnConfig<UserSearchResult>[]>(
    () => [
      {
        title: t('userSearch.passportNumber.label'),
        key: 'passportNumber',
        dataIndex: 'passportNumber',
        sorter: true,
      },
      {
        title: t('userSearch.employeeNumber.label'),
        key: 'employeeNumber',
        render: (_: any, record: UserSearchResult) =>
          record.employeeData?.employeeId || '-',
        sorter: false,
      },
      {
        title: t('userSearch.applicationNumber.label'),
        key: 'applicationNumber',
        dataIndex: 'applicationNumber',
        sorter: true,
      },
      {
        title: t('userSearch.uidNumber.label'),
        key: 'uidNumber',
        dataIndex: 'visaUidNumber',
        sorter: true,
      },
      {
        title: t('userSearch.eidNumber.label'),
        key: 'eidNumber',
        render: (_: any, record: UserSearchResult) =>
          record.eidData?.eidNumber || '-',
        sorter: false,
      },
      {
        title: t('common.actions'),
        key: 'actions',
        align: 'right',
        render: (_: any, record: any) => (
          <ActionButtons
            showEdit={false}
            showDelete={false}
            showView={rbac.hasAccess(userPermissions, 'visaApplication.view')}
            record={record}
            baseUrl="/dashboard/visa-application"
          />
        ),
      },
    ],
    [t, userPermissions]
  );

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('userSearch.title') })}
        showCreateButton={false}
        showBackButton={false}
      />
      <div className="row mt-3">
        <UserSearchComponent
          data={tableData as any}
          columns={columns}
          loading={isLoading || isFetching || isAvailableFiltersLoading}
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={false}
          onSearchChange={handleSearchChange}
          showFilters={false}
          availableFilters={undefined}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
          filters={filters}
          onSearch={handleSearch}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(UserSearchContainer, 'visaApplication.view');
